import { useState, useMemo } from 'react'
import InfoBox from '../../components/app/alertBox/infoBox'
import { DialogModal } from '../../components/common'
import Button from '../../components/common/buttons/Button'
import PriceBadge from '../../components/common/PriceBadge'
import SmartTable from '../../components/common/table/SmartTable'
import Icons from '../../components/common/icons'
import { calcWindowHeight } from '../../utilities/calcHeight'
import { useSnackbarManager } from '../../components/common/snackbar'
import { getErrorMessage } from '../../utilities/parsers'
import {
  completeAssignedClientFollowUp,
  scheduleAssignedClientFollowUp,
  proposeAssignedClientPackage,
} from './api'
import { updateClientPlanProposal } from '../Sales/api'
import { useAuthStore } from '../../store/authStore'

import moment from 'moment'

const formatDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '--'

const errorMessage = (error: any) => {
  const resData = error?.response?.data
  if (resData) {
    if (Array.isArray(resData.errors) && resData.errors.length) {
      return resData.errors.join(', ')
    }
    if (typeof resData.errors === 'string') return resData.errors
    if (typeof resData.error === 'string') return resData.error
    if (typeof resData.message === 'string') return resData.message
  }
  return getErrorMessage(error) || 'Request failed'
}

const toTitleCaseStr = (str?: string): string => {
  if (!str) return ''
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

type WorkflowProps = {
  assignment?: any
  proposedPackage?: any
  assignmentId?: string | number
  role?: string
  onRefresh?: (data?: any) => Promise<any> | void
  plans?: any[]
  showWorkflowActions?: boolean
  subscription?: any
  isServiceRole?: boolean
  onConfirmPackage?: () => void | Promise<void>
  confirmLoading?: boolean
  canConfirmPackage?: boolean
  canUpdatePackage?: boolean
  clientId?: string | number
}

export function ClientWorkflowDetails({
  assignment,
  proposedPackage,
  assignmentId,
  role,
  onRefresh,
  plans = [],
  showWorkflowActions = true,
  subscription,
  onConfirmPackage,
  confirmLoading,
  canConfirmPackage,
  canUpdatePackage,
  clientId,
}: WorkflowProps) {
  const { enqueueSnackbar } = useSnackbarManager()
  const authUserData = useAuthStore((s) => s.userData)
  const authRoleData = useAuthStore((s) => s.roleData)
  const loginRole = (
    authRoleData?.name ||
    (authUserData as any)?.role ||
    (authUserData as any)?.role_name ||
    ''
  )
    .toString()
    .toLowerCase()
  const isSales =
    String(role || loginRole || '')
      .toLowerCase()
      .includes('sales') ||
    (typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/sales'))
  const activeSub =
    subscription && subscription.status !== 'proposed' ? subscription : null
  const proposedPkg = proposedPackage

  const isSubscribed = Boolean(activeSub)
  const isPackageConfirmed = Boolean(
    activeSub ||
      subscription?.status === 'active' ||
      subscription?.status === 'confirmed' ||
      subscription?.status === 'package_confirmed' ||
      proposedPkg?.status === 'accepted' ||
      proposedPkg?.status === 'confirmed' ||
      proposedPkg?.status === 'active' ||
      assignment?.workflow_status === 'package_confirmed' ||
      assignment?.workflow_status === 'active'
  )

  const [proposalDialogOpen, setProposalDialogOpen] = useState(false)
  const [pkgSearch, setPkgSearch] = useState('')
  const [pkgPage, setPkgPage] = useState(1)
  const pkgPerPage = 4
  const [saving, setSaving] = useState(false)
  const [proposal, setProposal] = useState({
    plan_id: '',
    start_date: '',
    notes: '',
  })

  const openProposalModal = () => {
    if (!isSales || isPackageConfirmed) {
      enqueueSnackbar(
        isPackageConfirmed
          ? 'Confirmed packages cannot be modified.'
          : 'Only Sales can modify proposed packages.',
        {
          variant: 'warning',
        }
      )
      return
    }
    setProposal({
      plan_id: proposedPkg?.plan?.id ? String(proposedPkg.plan.id) : '',
      start_date: proposedPkg?.start_date || '',
      notes: proposedPkg?.notes || '',
    })
    setPkgSearch('')
    setPkgPage(1)
    setProposalDialogOpen(true)
  }

  const filteredPackages = useMemo(() => {
    if (!pkgSearch.trim()) return plans
    const q = pkgSearch.toLowerCase()
    return plans.filter((p: any) => {
      const nameMatch = p.name?.toLowerCase().includes(q)
      const catMatch = (p.category || p.plan_category)
        ?.toLowerCase()
        .includes(q)
      const priceMatch = String(
        p.discounted_sale_price || p.fees || p.price || p.amount || ''
      ).includes(q)
      return nameMatch || catMatch || priceMatch
    })
  }, [plans, pkgSearch])

  const pkgTotalPages = Math.max(
    1,
    Math.ceil(filteredPackages.length / pkgPerPage)
  )

  const visiblePackages = useMemo(() => {
    const start = (pkgPage - 1) * pkgPerPage
    return filteredPackages.slice(start, start + pkgPerPage)
  }, [filteredPackages, pkgPage, pkgPerPage])

  const selectedPlan = useMemo(() => {
    return plans.find(
      (item: any) => String(item.id) === String(proposal.plan_id)
    )
  }, [plans, proposal.plan_id])

  const submitProposal = async () => {
    if (!proposal.plan_id) {
      enqueueSnackbar('Please select a package plan.', { variant: 'error' })
      return
    }
    if (!proposal.start_date) {
      enqueueSnackbar('Choose a start date.', { variant: 'error' })
      return
    }
    const today = new Date().toISOString().split('T')[0]
    if (proposal.start_date < today) {
      enqueueSnackbar('Anticipated start date cannot be in the past.', {
        variant: 'error',
      })
      return
    }

    try {
      setSaving(true)
      if (assignmentId) {
        const response = await proposeAssignedClientPackage(
          assignmentId,
          proposal
        )
        enqueueSnackbar(
          response?.message || 'Package updated and activated successfully',
          {
            variant: 'success',
          }
        )
      } else if (clientId && proposedPkg?.id) {
        const response = await updateClientPlanProposal(
          clientId,
          proposedPkg.id,
          proposal,
          '/clients'
        )
        enqueueSnackbar(
          response?.message || 'Package proposal updated successfully',
          {
            variant: 'success',
          }
        )
      }
      setProposalDialogOpen(false)
      if (onRefresh) await onRefresh()
    } catch (error: any) {
      enqueueSnackbar(errorMessage(error), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleSecondaryAction = () => {
    setProposalDialogOpen(false)
  }

  const planName = isSubscribed
    ? (activeSub?.plan_name ?? activeSub?.name ?? activeSub?.plan?.name)
    : (proposedPkg?.plan?.name ?? proposedPkg?.name)
  const category = isSubscribed
    ? (activeSub?.plan_category ??
      activeSub?.category ??
      activeSub?.plan?.category)
    : (proposedPkg?.plan?.category ?? proposedPkg?.plan?.plan_category)
  const planId = isSubscribed
    ? (activeSub?.plan_id ?? activeSub?.plan?.id)
    : (proposedPkg?.plan?.id ?? proposedPkg?.plan_id)

  const startDateFormatted = isSubscribed
    ? activeSub?.start_date
      ? moment(activeSub.start_date).format('DD-MM-YYYY')
      : '--'
    : proposedPkg?.start_date
      ? moment(proposedPkg.start_date).format('DD-MM-YYYY')
      : '--'

  const endDateFormatted = isSubscribed
    ? activeSub?.end_date
      ? moment(activeSub.end_date).format('DD-MM-YYYY')
      : '--'
    : proposedPkg?.end_date
      ? moment(proposedPkg.end_date).format('DD-MM-YYYY')
      : '--'

  if (isSubscribed) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-50/90 via-teal-50/40 to-slate-50 border border-emerald-200/80 shadow-2xs transition-all">
        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-2xs">
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              Subscribed Package:
            </span>
            <span className="text-xs font-bold text-gray-900 truncate">
              {toTitleCaseStr(planName) || 'General Wellness'}
            </span>
            {category && (
              <span className="text-xs text-gray-500 font-medium">
                ({toTitleCaseStr(category)})
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold border ${
                String(activeSub?.status || '').toLowerCase() === 'active'
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  : String(activeSub?.status || '').toLowerCase() ===
                        'frozen' ||
                      String(activeSub?.status || '').toLowerCase() === 'paused'
                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  String(activeSub?.status || '').toLowerCase() === 'active'
                    ? 'bg-emerald-600'
                    : String(activeSub?.status || '').toLowerCase() ===
                          'frozen' ||
                        String(activeSub?.status || '').toLowerCase() ===
                          'paused'
                      ? 'bg-amber-600'
                      : 'bg-slate-500'
                }`}
              />
              {toTitleCaseStr(activeSub?.status || 'Active')}
            </span>
            <span className="text-xs text-gray-500 font-medium">
              {startDateFormatted} to {endDateFormatted}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {planId && (
            <a
              href={`/plans/${planId}`}
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
            >
              <span>View plan details</span>
              <span>→</span>
            </a>
          )}
        </div>
      </div>
    )
  }

  if (!proposedPkg) {
    return null
  }

  return (
    <>
      <section className="relative overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-5 shadow-sm">
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-indigo-100/60" />
        <div className="absolute -bottom-12 left-1/3 h-24 w-24 rounded-full bg-blue-100/50" />
        <div className="relative">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-blue-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-xl text-white shadow-sm">
                ✦
              </div>
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isSubscribed ? 'Subscribed package' : 'Proposed package'}
                  </h2>
                  <span
                    className={
                      isSubscribed
                        ? 'rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700'
                        : 'rounded-full bg-indigo-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700'
                    }
                  >
                    {isSubscribed ? 'Subscribed' : 'Proposed'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  {isSubscribed
                    ? 'The package is active and available in the calendar'
                    : 'Review the package details before activation'}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {planName && (
                <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-indigo-700 shadow-sm ring-1 ring-indigo-100">
                  {toTitleCaseStr(planName)}
                </span>
              )}
              {planId && (
                <a
                  href={`/plans/${planId}`}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline whitespace-nowrap"
                >
                  View plan details →
                </a>
              )}
            </div>
          </div>

          {activeSub || proposedPkg ? (
            <>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {activeSub ? (
                  <>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Package Plan
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {toTitleCaseStr(planName) || '--'}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Category
                      </div>
                      <div className="mt-1 text-sm font-semibold capitalize text-gray-800">
                        {category || '--'}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Start Date
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {startDateFormatted}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        End Date
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {endDateFormatted}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Assigned to
                      </div>
                      <div className="mt-1 text-sm font-semibold capitalize text-gray-800">
                        {assignment?.staff_name || '--'}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Proposed by
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800 capitalize">
                        {proposedPkg?.created_by?.name || 'Sales team'}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Duration
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {proposedPkg?.plan?.duration_days || '--'} days
                      </div>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3 ring-1 ring-blue-100">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Discount Fees
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {proposedPkg?.plan?.discounted_sale_price ??
                          proposedPkg?.plan?.fees ??
                          '--'}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {!activeSub && proposedPkg && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
                  <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-blue-100">
                    Start: <strong>{startDateFormatted}</strong>
                  </span>
                  <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-blue-100">
                    End: <strong>{endDateFormatted}</strong>
                  </span>
                </div>
              )}

              {proposedPkg?.notes && (
                <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <span className="font-semibold">Sales note:</span>{' '}
                  {proposedPkg.notes}
                </div>
              )}

              {activeSub && proposedPkg && (
                <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/70 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                        Upcoming Proposed Package
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {toTitleCaseStr(
                          proposedPkg?.plan?.name ||
                            proposedPkg?.name ||
                            'Proposed Package'
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span>
                        Start:{' '}
                        <strong>
                          {proposedPkg?.start_date
                            ? moment(proposedPkg.start_date).format(
                                'DD-MM-YYYY'
                              )
                            : '--'}
                        </strong>
                      </span>
                      <span>•</span>
                      <span>
                        End:{' '}
                        <strong>
                          {proposedPkg?.end_date
                            ? moment(proposedPkg.end_date).format('DD-MM-YYYY')
                            : '--'}
                        </strong>
                      </span>
                    </div>
                  </div>
                  {proposedPkg?.notes && (
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="font-semibold text-gray-700">
                        Sales note:
                      </span>{' '}
                      {proposedPkg.notes}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-end gap-2">
                    {isSales &&
                      !isPackageConfirmed &&
                      (canUpdatePackage ??
                        (showWorkflowActions &&
                          assignment?.can_confirm_package)) && (
                        <Button
                          outlined
                          size="xs"
                          icon="edit"
                          className="rounded-lg !border-indigo-500 !text-indigo-700 hover:!bg-indigo-50"
                          label="Update package"
                          onClick={openProposalModal}
                          disabled={saving || !plans.length}
                        />
                      )}
                    {onConfirmPackage && (canConfirmPackage ?? true) && (
                      <Button
                        className="primaryButton"
                        size="xs"
                        label={
                          confirmLoading ? 'Confirming...' : 'Confirm Package'
                        }
                        disabled={confirmLoading}
                        onClick={onConfirmPackage}
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-blue-100 pt-4">
                <p className="text-xs text-gray-500">
                  {isSubscribed
                    ? 'Active subscription package details.'
                    : 'Review the proposed package. You can update details or confirm package to activate.'}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  {!activeSub && !isPackageConfirmed && proposedPkg && (
                    <>
                      {isSales &&
                        (canUpdatePackage ??
                          (showWorkflowActions &&
                            assignment?.can_confirm_package)) && (
                          <Button
                            outlined
                            size="xs"
                            icon="edit"
                            className="rounded-lg !border-indigo-500 !text-indigo-700 hover:!bg-indigo-50"
                            label="Update and Confirm"
                            onClick={openProposalModal}
                            disabled={saving || !plans.length}
                          />
                        )}
                      {onConfirmPackage && (canConfirmPackage ?? true) && (
                        <Button
                          className="primaryButton"
                          size="xs"
                          label={
                            confirmLoading ? 'Confirming...' : 'Confirm Package'
                          }
                          disabled={confirmLoading}
                          onClick={onConfirmPackage}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <InfoBox content="No proposed package is available yet." />
            </div>
          )}
        </div>
      </section>

      <DialogModal
        isOpen={proposalDialogOpen}
        onClose={() => {
          setProposalDialogOpen(false)
        }}
        title={
          proposedPkg || activeSub || assignment?.anticipated_package
            ? 'Update package'
            : 'Select package'
        }
        subTitle="Select a package, set the start date, and confirm it."
        onSubmit={submitProposal}
        actionLabel="Confirm package"
        actionLoader={saving}
        secondaryAction={handleSecondaryAction}
        secondaryActionLabel="Cancel"
        small={false}
        className="w-full max-w-4xl"
        body={
          <div>
            <>
              <div>
                <div className="relative mb-4">
                  <Icons
                    name="search"
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={pkgSearch}
                    onChange={(e) => {
                      setPkgSearch(e.target.value)
                      setPkgPage(1)
                    }}
                    placeholder="Search packages by name"
                    className="w-full rounded-lg border border-formBorder bg-cardWrapperBg py-2.5 pl-10 pr-4 text-sm text-primaryText placeholder-gray-400 outline-none focus:border-primaryGreen focus:bg-white focus:ring-2 focus:ring-primaryGreen/20 transition"
                  />
                </div>
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-primaryText">
                    {filteredPackages.length} package
                    {filteredPackages.length === 1 ? '' : 's'} found
                  </span>
                </div>
                <div className="space-y-2.5">
                  {visiblePackages.map((pkg: any) => {
                    const isSelected =
                      String(proposal.plan_id) === String(pkg.id)
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => {
                          setProposal((prev) => ({
                            ...prev,
                            plan_id: String(pkg.id),
                          }))
                        }}
                        className={
                          'relative w-full rounded-xl border-2 p-4 text-left transition-all duration-200 hover:shadow-md ' +
                          (isSelected
                            ? 'border-primaryGreen shadow-md'
                            : 'border-formBorder bg-white hover:border-primaryGreen/40 hover:bg-cardWrapperBg')
                        }
                        style={
                          isSelected
                            ? {
                                background:
                                  'linear-gradient(135deg, #e6fbfc 0%, #f0fffe 50%, #e0f7fa 100%)',
                              }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={
                              'h-12 w-12 shrink-0 rounded-xl overflow-hidden transition-colors flex items-center justify-center ' +
                              (isSelected
                                ? 'bg-primaryGreen text-white shadow-sm'
                                : 'bg-cardWrapperBg text-primaryGreen')
                            }
                          >
                            {pkg.thumbnail_url ? (
                              <img
                                src={pkg.thumbnail_url}
                                alt={pkg.name || ''}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Icons name="package" className="h-6 w-6" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={
                                  'truncate text-sm font-semibold ' +
                                  (isSelected
                                    ? 'text-primaryGreen'
                                    : 'text-primaryText')
                                }
                              >
                                {(() => {
                                  const name = pkg.name || 'Unnamed'
                                  return (
                                    name.charAt(0).toUpperCase() + name.slice(1)
                                  )
                                })()}
                              </span>
                              {(pkg.category || pkg.plan_category) && (
                                <span
                                  className={
                                    'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ' +
                                    (isSelected
                                      ? 'bg-primaryGreen/15 text-primaryGreen'
                                      : 'bg-cardWrapperBg text-secondary')
                                  }
                                >
                                  {typeof (
                                    pkg.category || pkg.plan_category
                                  ) === 'object'
                                    ? pkg.category?.name ||
                                      pkg.plan_category?.name ||
                                      ''
                                    : String(
                                        pkg.category || pkg.plan_category || ''
                                      )}
                                </span>
                              )}
                            </div>
                            {pkg.description && (
                              <p className="mt-0.5 line-clamp-1 text-xs text-secondary">
                                {pkg.description}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            {pkg.duration_days && (
                              <span
                                className={
                                  'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ' +
                                  (isSelected
                                    ? 'bg-primaryGreen/10 text-primaryGreen'
                                    : 'bg-cardWrapperBg text-gray-500')
                                }
                              >
                                <svg
                                  className="h-3.5 w-3.5 shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <span>{pkg.duration_days} days</span>
                              </span>
                            )}
                            <PriceBadge
                              actualPrice={pkg.actual_price}
                              discountedPrice={pkg.discounted_sale_price}
                              fees={pkg.fees}
                              price={pkg.price}
                              amount={pkg.amount}
                              variant={isSelected ? 'selected' : 'default'}
                            />
                          </div>
                          {isSelected && (
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primaryGreen shadow-sm">
                              <svg
                                className="h-4 w-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })}
                  {filteredPackages.length === 0 && (
                    <div className="rounded-lg border border-dashed border-formBorder p-8 text-center text-sm text-secondary">
                      {pkgSearch
                        ? 'No packages match your search.'
                        : 'No active packages available.'}
                    </div>
                  )}
                </div>
                {pkgTotalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t border-formBorder pt-3">
                    <span className="text-xs text-secondary">
                      Page {Math.min(pkgPage, pkgTotalPages)} of {pkgTotalPages}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={pkgPage <= 1}
                        onClick={() => setPkgPage((p) => Math.max(1, p - 1))}
                        className="rounded-lg border border-formBorder px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={pkgPage >= pkgTotalPages}
                        onClick={() =>
                          setPkgPage((p) => Math.min(pkgTotalPages, p + 1))
                        }
                        className="rounded-lg border border-formBorder px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {selectedPlan && (
                  <div className="rounded-lg border border-formBorder bg-cardWrapperBg p-4">
                    <div className="text-xs text-secondary">
                      Selected package
                    </div>
                    <div className="mt-1 text-sm font-medium text-primaryText">
                      {selectedPlan.name
                        ? selectedPlan.name.charAt(0).toUpperCase() +
                          selectedPlan.name.slice(1)
                        : ''}
                      {selectedPlan.category || selectedPlan.plan_category
                        ? ` — ${
                            typeof (
                              selectedPlan.category ||
                              selectedPlan.plan_category
                            ) === 'object'
                              ? selectedPlan.category?.name ||
                                selectedPlan.plan_category?.name
                              : selectedPlan.category ||
                                selectedPlan.plan_category
                          }`
                        : ''}
                      {selectedPlan.duration_days
                        ? ` — ${selectedPlan.duration_days} days`
                        : ''}
                    </div>
                    {(selectedPlan.discounted_sale_price ||
                      selectedPlan.fees ||
                      selectedPlan.price ||
                      selectedPlan.amount) && (
                      <div className="mt-2">
                        <PriceBadge
                          actualPrice={selectedPlan.actual_price}
                          discountedPrice={selectedPlan.discounted_sale_price}
                          fees={selectedPlan.fees}
                          price={selectedPlan.price}
                          amount={selectedPlan.amount}
                          variant="selected"
                          size="md"
                        />
                      </div>
                    )}
                  </div>
                )}
                <label className="block text-sm font-medium text-gray-700">
                  Anticipated start date <span className="text-red-500">*</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-formBorder p-2.5 text-sm outline-none focus:border-primaryGreen focus:ring-2 focus:ring-primaryGreen/20"
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={proposal.start_date}
                    onChange={(event) =>
                      setProposal({
                        ...proposal,
                        start_date: event.target.value,
                      })
                    }
                  />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                  <textarea
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-formBorder p-2.5 text-sm outline-none focus:border-primaryGreen focus:ring-2 focus:ring-primaryGreen/20"
                    placeholder="Optional notes for this proposed assignment"
                    value={proposal.notes}
                    onChange={(event) =>
                      setProposal({ ...proposal, notes: event.target.value })
                    }
                  />
                </label>
              </div>
            </>
          </div>
        }
      />
    </>
  )
}

export function ClientWorkflowFollowUps({
  assignment,
  assignmentId,
  onRefresh,
  readOnly = false,
}: Omit<WorkflowProps, 'role'> & { readOnly?: boolean }) {
  const { enqueueSnackbar } = useSnackbarManager()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [followUp, setFollowUp] = useState({ scheduled_at: '', notes: '' })
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false)
  const [selectedFollowUp, setSelectedFollowUp] = useState<any>(null)
  const [completionNotes, setCompletionNotes] = useState('')

  const run = async (action: () => Promise<any>, success: string) => {
    try {
      setSaving(true)
      const response = await action()
      enqueueSnackbar(response?.message || success, { variant: 'success' })
      if (onRefresh) await onRefresh()
      return true
    } catch (error: any) {
      enqueueSnackbar(errorMessage(error), { variant: 'error' })
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleCloseSchedule = () => {
    setDialogOpen(false)
    setFollowUp({ scheduled_at: '', notes: '' })
  }

  const handleOpenSchedule = () => {
    setFollowUp({ scheduled_at: '', notes: '' })
    setDialogOpen(true)
  }

  const handleCloseCompletion = () => {
    setCompletionDialogOpen(false)
    setSelectedFollowUp(null)
    setCompletionNotes('')
  }

  const submitFollowUp = async () => {
    if (!assignmentId) return
    if (!followUp.scheduled_at) {
      enqueueSnackbar('Choose a date and time for the follow-up.', {
        variant: 'error',
      })
      return
    }
    const ok = await run(
      () =>
        scheduleAssignedClientFollowUp(assignmentId, {
          ...followUp,
          scheduled_at: new Date(followUp.scheduled_at).toISOString(),
        }),
      'Follow-up scheduled successfully'
    )
    if (ok) {
      handleCloseSchedule()
    }
  }

  const submitCompletion = async () => {
    if (!completionNotes.trim()) {
      enqueueSnackbar('Please enter follow-up remarks.', { variant: 'error' })
      return
    }
    if (!selectedFollowUp) return
    const ok = await run(
      () =>
        completeAssignedClientFollowUp(
          selectedFollowUp.assignment_id || assignmentId,
          selectedFollowUp.id,
          { completion_notes: completionNotes.trim() }
        ),
      'Follow-up marked as completed'
    )
    if (ok) {
      handleCloseCompletion()
    }
  }

  return (
    <>
      <section className="w-full">
        {!readOnly && (
          <div className="flex items-center justify-end gap-3 mb-3">
            <Button
              outlined
              label="Schedule follow-up"
              onClick={handleOpenSchedule}
              disabled={saving}
            />
          </div>
        )}
        <SmartTable
          data={assignment?.follow_ups || []}
          dataRowKey="id"
          columns={[
            {
              title: 'Scheduled date',
              field: 'scheduled_at',
              customCell: true,
              renderCell: (row: any) => ({
                cell: formatDate(row.scheduled_at),
              }),
              isVisible: true,
            },
            {
              title: 'Staff',
              field: 'assigned_staff_name',
              customCell: true,
              renderCell: (row: any) => {
                const staffName =
                  row.scheduled_by?.name ||
                  row.created_by?.name ||
                  row.assigned_staff_name ||
                  row.staff_name ||
                  assignment?.staff_name
                const staffRole =
                  row.scheduled_by?.role ||
                  row.created_by?.role ||
                  row.assigned_staff_role ||
                  row.staff_role ||
                  assignment?.role ||
                  'service team'
                return {
                  cell: staffName ? `${staffName} (${staffRole})` : '--',
                }
              },
              isVisible: true,
            },
            {
              title: 'Notes',
              field: 'notes',
              customCell: true,
              renderCell: (row: any) => ({
                cell: row.notes || '--',
                toolTip: row.notes || '',
              }),
              isVisible: true,
            },
            {
              title: 'Completion remarks',
              field: 'completion_notes',
              customCell: true,
              renderCell: (row: any) => ({
                cell: row.completion_notes || '--',
                toolTip: row.completion_notes || '',
              }),
              isVisible: true,
            },
            {
              title: 'Status',
              field: 'status',
              customCell: true,
              renderCell: (row: any) => ({
                cell: (
                  <span
                    className={
                      row.status === 'completed'
                        ? 'capitalize text-green-700'
                        : 'capitalize text-amber-700'
                    }
                  >
                    {row.status || 'scheduled'}
                  </span>
                ),
              }),
              isVisible: true,
            },
          ]}
          actionProps={
            readOnly
              ? []
              : [
                  {
                    title: 'Mark completed',
                    toolTip: 'Complete follow-up',
                    icon: <Icons name="check-circle" />,
                    variant: 'success',
                    action: (row: any) => {
                      setSelectedFollowUp(row)
                      setCompletionNotes('')
                      setCompletionDialogOpen(true)
                    },
                    hide: (row: any) =>
                      String(row?.status || '').toLowerCase() !== 'scheduled',
                    disabled: () => saving,
                  },
                ]
          }
          externalActions
          toolbar
          columnToggle
          height={calcWindowHeight(
            (assignment?.follow_ups || []).length ? 390 : 420
          )}
          emptyTitle="No follow-ups scheduled"
        />
      </section>

      <DialogModal
        isOpen={dialogOpen}
        onClose={handleCloseSchedule}
        title="Schedule follow-up"
        subTitle="Choose the date and time for the client call."
        onSubmit={submitFollowUp}
        actionLabel="Schedule"
        actionLoader={saving}
        secondaryAction={handleCloseSchedule}
        secondaryActionLabel="Cancel"
        body={
          <div className="space-y-4">
            <label className="block text-sm">
              Date and time <span className="text-red-500 ml-0.5">*</span>
              <input
                className="mt-1 w-full border rounded-lg p-2"
                type="datetime-local"
                value={followUp.scheduled_at}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(event) =>
                  setFollowUp({ ...followUp, scheduled_at: event.target.value })
                }
              />
            </label>
            <label className="block text-sm">
              Notes
              <textarea
                className="mt-1 w-full border rounded-lg p-2"
                rows={3}
                value={followUp.notes}
                onChange={(event) =>
                  setFollowUp({ ...followUp, notes: event.target.value })
                }
              />
            </label>
          </div>
        }
      />
      <DialogModal
        isOpen={completionDialogOpen}
        onClose={handleCloseCompletion}
        title="Complete follow-up"
        subTitle="Add remarks about the completed client follow-up."
        onSubmit={submitCompletion}
        actionLabel="Save remarks"
        actionLoader={saving}
        actionDisabled={!completionNotes.trim()}
        secondaryAction={handleCloseCompletion}
        secondaryActionLabel="Cancel"
        body={
          <label className="block text-sm">
            Follow-up remarks <span className="text-red-500 ">*</span>
            <textarea
              className="mt-1 w-full border rounded-lg p-2"
              rows={4}
              value={completionNotes}
              onChange={(event) => setCompletionNotes(event.target.value)}
              placeholder="Enter the outcome or remarks"
            />
          </label>
        }
      />
    </>
  )
}
